import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { TermsCheckbox } from "@/components/terms-checkbox"
import { SsoButtons } from "@/components/sso-buttons"
import { toast } from "sonner"
import { getMetaValue } from "@/utils/meta.utils"
import { useState } from "react"
import { useTranslation } from "react-i18next"

type LoginFormProps = {
  canSignInWithPassword: boolean
  canSignInWithOpenidConnect: boolean
  openidConnectProvider: string | null
  canSignInWithGoogle: boolean
  canSignInWithMicrosoft: boolean
} & React.ComponentProps<"div">

export const LoginForm = ({
  canSignInWithPassword,
  canSignInWithOpenidConnect,
  openidConnectProvider,
  canSignInWithGoogle,
  canSignInWithMicrosoft,
  className,
  ...props
}: LoginFormProps) => {
  const [isEmailLoginLoading, setIsEmailLoginLoading] = useState(false)
  const [isSsoLoading, setIsSsoLoading] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const { t } = useTranslation()

  const isAnyAuthLoading = isEmailLoginLoading || isSsoLoading
  const canSignInWithOAuth = canSignInWithOpenidConnect || canSignInWithGoogle || canSignInWithMicrosoft

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isAnyAuthLoading) return

    setIsEmailLoginLoading(true)
    const formData = new FormData(e.currentTarget)

    try {
      const response = await fetch("/users/sign_in", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getMetaValue("csrf-token") || ""
        },
        body: JSON.stringify({
          user: { email: formData.get("email"), password: formData.get("password") }
        }),
        credentials: "same-origin"
      })

      if (response.ok) {
        const data = await response.json().catch(() => ({}))
        window.location.href = data.redirect_to || "/"
      } else {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error || t("landing.login.errors.invalid_credentials")
        toast.warning(errorMessage)
      }
    } catch {
      toast.error(t("landing.login.errors.login_failed"))
    } finally {
      setIsEmailLoginLoading(false)
    }
  }

  const termsCheckbox = <TermsCheckbox accepted={termsAccepted} onAcceptedChange={setTermsAccepted} />

  return (
    <div className={cn("flex flex-col gap-16", className)} {...props}>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">{t("landing.login.title")}</h1>
        <p className="text-muted-foreground text-sm text-balance">
          {canSignInWithOpenidConnect && canSignInWithPassword && t("landing.login.subtitle_both")}
          {!canSignInWithOpenidConnect && canSignInWithPassword && t("landing.login.subtitle_email")}
        </p>
      </div>

      <div className="grid gap-6">
        {canSignInWithOAuth && termsCheckbox}

        <SsoButtons
          ssoProviders={{
            google: canSignInWithGoogle,
            microsoft: canSignInWithMicrosoft,
            openidConnect: canSignInWithOpenidConnect,
            openidConnectProvider
          }}
          termsAccepted={termsAccepted}
          disabled={isEmailLoginLoading}
          onLoadingChange={setIsSsoLoading}
        />

        {canSignInWithOAuth && canSignInWithPassword && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{t("landing.login.or")}</span>
            </div>
          </div>
        )}

        {canSignInWithPassword && (
          <form onSubmit={handleEmailSubmit} className="grid gap-6">
            <div className="grid gap-3">
              <Label htmlFor="email">{t("landing.login.email")}</Label>
              <Input
                name="email"
                id="email"
                type="email"
                placeholder="m@example.com"
                autoComplete="email"
                required
                disabled={isAnyAuthLoading}
              />
            </div>
            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("landing.login.password")}</Label>
                <a
                  href="/users/password/new"
                  className="text-xs text-muted-foreground hover:text-primary hover:underline"
                >
                  {t("landing.login.forgot_password")}
                </a>
              </div>
              <Input
                name="password"
                id="password"
                type="password"
                autoComplete="current-password"
                required
                disabled={isAnyAuthLoading}
              />
            </div>
            {!canSignInWithOAuth && termsCheckbox}
            <Button type="submit" className="w-full" disabled={!termsAccepted || isAnyAuthLoading}>
              {isEmailLoginLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {t("landing.login.signing_in")}
                </>
              ) : (
                t("landing.login.sign_in_with_email")
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
